import React, { useState } from 'react';
import { Sun, Flame, Home, Building, Check, X, Zap, Calendar, Clock, Info, ChevronRight, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const QuoteForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  const totalSteps = 5;
  const progress = (currentStep / totalSteps) * 100;

  // Step 1: Type de projet
  const renderStep1 = () => (
    <div>
      <h2 className="heading-2" style={{ color: 'var(--text-primary)', marginBottom: '40px', textAlign: 'center' }}>
        Quel type de projet vous intéresse ?
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {[
          { value: 'solaire', label: 'Énergie Solaire', icon: Sun },
          { value: 'chauffage', label: 'Chauffage Écologique', icon: Flame },
          { value: 'isolation', label: 'Isolation Thermique', icon: Home }
        ].map((option) => {
          const Icon = option.icon;
          const isSelected = formData.typeProjet === option.value;
          return (
            <button
              key={option.value}
              onClick={() => {
                setFormData({ ...formData, typeProjet: option.value });
                setTimeout(() => setCurrentStep(2), 300);
              }}
              style={{
                padding: '32px 24px',
                border: `2px solid ${isSelected ? 'var(--brand-primary)' : 'var(--border-light)'}`,
                borderRadius: '25px',
                background: isSelected ? 'var(--bg-subtle)' : 'white',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: isSelected ? 'var(--brand-primary)' : 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Icon size={24} style={{ color: isSelected ? 'var(--brand-dark)' : 'var(--text-primary)' }} />
              </div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>{option.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Step 2: Type de logement
  const renderStep2 = () => (
    <div>
      <h2 className="heading-2" style={{ color: 'var(--text-primary)', marginBottom: '40px', textAlign: 'center' }}>
        Quel est votre type de logement ?
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {[
          { value: 'maison', label: 'Une maison', icon: Home },
          { value: 'appartement', label: 'Un appartement', icon: Building }
        ].map((option) => {
          const Icon = option.icon;
          const isSelected = formData.typeLogement === option.value;
          return (
            <button
              key={option.value}
              onClick={() => {
                setFormData({ ...formData, typeLogement: option.value });
                setTimeout(() => setCurrentStep(3), 300);
              }}
              style={{
                padding: '32px 24px',
                border: `2px solid ${isSelected ? 'var(--brand-primary)' : 'var(--border-light)'}`,
                borderRadius: '25px',
                background: isSelected ? 'var(--bg-subtle)' : 'white',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: isSelected ? 'var(--brand-primary)' : 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Icon size={24} style={{ color: isSelected ? 'var(--brand-dark)' : 'var(--text-primary)' }} />
              </div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>{option.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Step 3: Propriétaire
  const renderStep3 = () => (
    <div>
      <h2 className="heading-2" style={{ color: 'var(--text-primary)', marginBottom: '40px', textAlign: 'center' }}>
        Êtes-vous propriétaire de votre logement ?
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {[
          { value: 'oui', label: 'Oui', icon: Check },
          { value: 'non', label: 'Non', icon: X }
        ].map((option) => {
          const Icon = option.icon;
          const isSelected = formData.proprietaire === option.value;
          return (
            <button
              key={option.value}
              onClick={() => {
                setFormData({ ...formData, proprietaire: option.value });
                setTimeout(() => setCurrentStep(4), 300);
              }}
              style={{
                padding: '32px 24px',
                border: `2px solid ${isSelected ? 'var(--brand-primary)' : 'var(--border-light)'}`,
                borderRadius: '25px',
                background: isSelected ? 'var(--bg-subtle)' : 'white',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: isSelected ? 'var(--brand-primary)' : 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Icon size={24} style={{ color: isSelected ? 'var(--brand-dark)' : 'var(--text-primary)' }} />
              </div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>{option.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Step 4: Délai
  const renderStep4 = () => (
    <div>
      <h2 className="heading-2" style={{ color: 'var(--text-primary)', marginBottom: '40px', textAlign: 'center' }}>
        Quand souhaitez-vous réaliser vos travaux ?
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {[
          { value: 'immediat', label: 'Le plus tôt possible', icon: Zap },
          { value: '3mois', label: 'Dans les 3 mois', icon: Calendar },
          { value: '6mois', label: 'Dans les 6 mois', icon: Clock },
          { value: 'etude', label: 'Je me renseigne', icon: Info }
        ].map((option) => {
          const Icon = option.icon;
          const isSelected = formData.delai === option.value;
          return (
            <button
              key={option.value}
              onClick={() => {
                setFormData({ ...formData, delai: option.value });
                setTimeout(() => setCurrentStep(5), 300);
              }}
              style={{
                padding: '32px 24px',
                border: `2px solid ${isSelected ? 'var(--brand-primary)' : 'var(--border-light)'}`,
                borderRadius: '25px',
                background: isSelected ? 'var(--bg-subtle)' : 'white',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: isSelected ? 'var(--brand-primary)' : 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Icon size={24} style={{ color: isSelected ? 'var(--brand-dark)' : 'var(--text-primary)' }} />
              </div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>{option.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Step 5: Coordonnées
  const renderStep5 = () => {
    const validateForm = () => {
      const newErrors = {};
      if (!formData.nom) newErrors.nom = 'Requis';
      if (!formData.prenom) newErrors.prenom = 'Requis';
      if (!formData.email) newErrors.email = 'Requis';
      if (!formData.telephone) newErrors.telephone = 'Requis';
      if (!formData.codePostal) newErrors.codePostal = 'Requis';
      if (!formData.ville) newErrors.ville = 'Requis';
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
      e.preventDefault();

      if (!validateForm()) {
        toast.error('Veuillez corriger les erreurs dans le formulaire');
        return;
      }

      // Send to backend
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      fetch(`${BACKEND_URL}/api/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      })
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            console.log('Quote submitted:', data);
            toast.success('Votre demande de devis a été envoyée avec succès ! Nous vous contacterons sous 24h.');
            
            // Reset
            setTimeout(() => {
              setFormData({});
              setCurrentStep(1);
            }, 2000);
          } else {
            toast.error('Erreur lors de l envoi de la demande');
          }
        })
        .catch(error => {
          console.error('Error:', error);
          toast.error('Erreur lors de l envoi de la demande');
        });
    };

    return (
      <div>
        <h2 className="heading-2" style={{ color: 'var(--text-primary)', marginBottom: '32px', textAlign: 'center' }}>
          Vos coordonnées
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '16px', fontWeight: '500', color: 'var(--text-primary)' }}>
                Nom <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                value={formData.nom || ''}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                style={{ width: '100%', padding: '12px 16px', border: `2px solid ${errors.nom ? 'red' : 'var(--border-light)'}`, borderRadius: '12px', fontSize: '16px', outline: 'none' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '16px', fontWeight: '500', color: 'var(--text-primary)' }}>
                Prénom <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                value={formData.prenom || ''}
                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                style={{ width: '100%', padding: '12px 16px', border: `2px solid ${errors.prenom ? 'red' : 'var(--border-light)'}`, borderRadius: '12px', fontSize: '16px', outline: 'none' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '16px', fontWeight: '500', color: 'var(--text-primary)' }}>
                Email <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={{ width: '100%', padding: '12px 16px', border: `2px solid ${errors.email ? 'red' : 'var(--border-light)'}`, borderRadius: '12px', fontSize: '16px', outline: 'none' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '16px', fontWeight: '500', color: 'var(--text-primary)' }}>
                Téléphone <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="tel"
                value={formData.telephone || ''}
                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                style={{ width: '100%', padding: '12px 16px', border: `2px solid ${errors.telephone ? 'red' : 'var(--border-light)'}`, borderRadius: '12px', fontSize: '16px', outline: 'none' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '16px', fontWeight: '500', color: 'var(--text-primary)' }}>
                Code postal <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                value={formData.codePostal || ''}
                onChange={(e) => setFormData({ ...formData, codePostal: e.target.value })}
                style={{ width: '100%', padding: '12px 16px', border: `2px solid ${errors.codePostal ? 'red' : 'var(--border-light)'}`, borderRadius: '12px', fontSize: '16px', outline: 'none' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '16px', fontWeight: '500', color: 'var(--text-primary)' }}>
                Ville <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                value={formData.ville || ''}
                onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                style={{ width: '100%', padding: '12px 16px', border: `2px solid ${errors.ville ? 'red' : 'var(--border-light)'}`, borderRadius: '12px', fontSize: '16px', outline: 'none' }}
              />
            </div>
          </div>
          <button type="submit" className="btn-primary" style={{ width: '100%', marginTop: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            Envoyer ma demande de devis
            <ChevronRight size={20} />
          </button>
        </form>
      </div>
    );
  };

  return (
    <div style={{ background: 'var(--bg-page)', minHeight: '100vh' }}>
      <section style={{ paddingTop: '140px', paddingBottom: '64px' }}>
        <div className="container" style={{ maxWidth: '900px' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <h1 className="display-medium" style={{ color: 'var(--text-primary)', marginBottom: '16px' }}>
              Obtenez Votre Devis Gratuit
            </h1>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Répondez à quelques questions et recevez votre devis personnalisé en 24h
            </p>
          </div>

          {/* Progress Bar */}
          <div style={{ width: '100%', height: '8px', background: 'var(--bg-subtle)', borderRadius: '8px', overflow: 'hidden', marginBottom: '48px' }}>
            <div style={{ width: `${progress}%`, height: '100%', background: 'var(--brand-primary)', transition: 'width 0.3s ease' }} />
          </div>

          {/* Step Indicator */}
          <div style={{ textAlign: 'center', marginBottom: '32px', fontSize: '14px', color: 'var(--text-light)' }}>
            Étape {currentStep} sur {totalSteps}
          </div>

          {/* Form Card */}
          <div className="network-card" style={{ padding: 'var(--spacing-giant)', minHeight: '400px' }}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}
          </div>

          {/* Navigation Buttons */}
          {currentStep > 1 && currentStep < 5 && (
            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="btn-secondary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                <ChevronLeft size={20} />
                Retour
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default QuoteForm;
